import { useCallback, useRef, useEffect } from 'react';

// Chess sound effects using Web Audio API for low latency
const SOUND_URLS = {
  move: 'https://assets.mixkit.co/active_storage/sfx/2571/2571-preview.mp3', // Soft click
  capture: 'https://assets.mixkit.co/active_storage/sfx/2568/2568-preview.mp3', // Impact
  check: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3', // Alert
  castle: 'https://assets.mixkit.co/active_storage/sfx/2572/2572-preview.mp3', // Double click
  gameStart: 'https://assets.mixkit.co/active_storage/sfx/2570/2570-preview.mp3', // Ready
  gameEnd: 'https://assets.mixkit.co/active_storage/sfx/1435/1435-preview.mp3', // Victory/End
  lowTime: 'https://assets.mixkit.co/active_storage/sfx/2867/2867-preview.mp3', // Warning tick
  drawOffer: 'https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3', // Notification
};

// Generate synthetic sounds using Web Audio API for reliability
const createOscillatorSound = (audioContext, frequency, duration, type = 'sine') => {
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator.frequency.value = frequency;
  oscillator.type = type;
  
  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
  
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + duration);
};

// Neutral wooden "tock" (piece set down on a board): filtered noise burst + soft low thump
const createTock = (audioContext, { gain = 0.5, tone = 1400, thump = 160, duration = 0.07 } = {}) => {
  const now = audioContext.currentTime;
  const frames = Math.floor(audioContext.sampleRate * duration);
  const buffer = audioContext.createBuffer(1, frames, audioContext.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / frames, 3);
  const noise = audioContext.createBufferSource();
  noise.buffer = buffer;
  const filter = audioContext.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = tone;
  const noiseGain = audioContext.createGain();
  noiseGain.gain.setValueAtTime(gain, now);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + duration);
  noise.connect(filter).connect(noiseGain).connect(audioContext.destination);
  noise.start(now);

  const osc = audioContext.createOscillator();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(thump, now);
  osc.frequency.exponentialRampToValueAtTime(thump * 0.6, now + 0.05);
  const oscGain = audioContext.createGain();
  oscGain.gain.setValueAtTime(gain * 0.5, now);
  oscGain.gain.exponentialRampToValueAtTime(0.001, now + 0.06);
  osc.connect(oscGain).connect(audioContext.destination);
  osc.start(now);
  osc.stop(now + 0.07);
};

export function useChessSounds(enabled = true) {
  const audioContextRef = useRef(null);
  const soundsLoadedRef = useRef(false);
  const audioBuffersRef = useRef({});
  
  // Initialize Audio Context
  useEffect(() => {
    if (!enabled) return;
    
    const initAudio = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
    };
    
    // Initialize on first user interaction
    const handleInteraction = () => {
      initAudio();
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
    
    document.addEventListener('click', handleInteraction);
    document.addEventListener('keydown', handleInteraction);
    
    return () => {
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
    };
  }, [enabled]);

  // Play synthetic sound (fallback, always works)
  const playSyntheticSound = useCallback((type) => {
    if (!enabled) return;
    
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      
      switch (type) {
        case 'move':
          createTock(ctx);
          break;
        case 'capture':
          createTock(ctx, { gain: 0.7, tone: 900, thump: 120, duration: 0.09 });
          break;
        case 'check':
          // Alert beep
          createOscillatorSound(ctx, 880, 0.1, 'sine');
          setTimeout(() => createOscillatorSound(ctx, 1100, 0.15, 'sine'), 100);
          break;
        case 'castle':
          createTock(ctx, { gain: 0.4 });
          setTimeout(() => createTock(ctx, { gain: 0.4 }), 90);
          break;
        case 'gameStart':
          // Rising tone
          createOscillatorSound(ctx, 440, 0.15, 'sine');
          setTimeout(() => createOscillatorSound(ctx, 660, 0.15, 'sine'), 150);
          setTimeout(() => createOscillatorSound(ctx, 880, 0.2, 'sine'), 300);
          break;
        case 'gameEnd':
          // Fanfare
          createOscillatorSound(ctx, 523, 0.2, 'sine');
          setTimeout(() => createOscillatorSound(ctx, 659, 0.2, 'sine'), 200);
          setTimeout(() => createOscillatorSound(ctx, 784, 0.3, 'sine'), 400);
          break;
        case 'lowTime':
          // Tick
          createOscillatorSound(ctx, 1000, 0.03, 'square');
          break;
        case 'drawOffer':
          // Notification
          createOscillatorSound(ctx, 600, 0.1, 'sine');
          setTimeout(() => createOscillatorSound(ctx, 800, 0.1, 'sine'), 120);
          break;
        case 'illegal':
          // Error buzz
          createOscillatorSound(ctx, 150, 0.15, 'sawtooth');
          break;
        case 'victory':
          // Victory fanfare
          [523, 659, 784, 1047].forEach((freq, i) => {
            setTimeout(() => createOscillatorSound(ctx, freq, 0.25, 'sine'), i * 150);
          });
          break;
        case 'defeat':
          // Sad trombone
          [400, 350, 300, 250].forEach((freq, i) => {
            setTimeout(() => createOscillatorSound(ctx, freq, 0.2, 'sawtooth'), i * 200);
          });
          break;
        default:
          createOscillatorSound(ctx, 500, 0.05, 'sine');
      }
    } catch (err) {
      console.warn('Sound playback failed:', err);
    }
  }, [enabled]);

  // Main play function
  const playSound = useCallback((type) => {
    if (!enabled) return;
    playSyntheticSound(type);
  }, [enabled, playSyntheticSound]);

  // Convenience methods
  const sounds = {
    move: () => playSound('move'),
    capture: () => playSound('capture'),
    check: () => playSound('check'),
    castle: () => playSound('castle'),
    gameStart: () => playSound('gameStart'),
    gameEnd: () => playSound('gameEnd'),
    lowTime: () => playSound('lowTime'),
    drawOffer: () => playSound('drawOffer'),
    illegal: () => playSound('illegal'),
    victory: () => playSound('victory'),
    defeat: () => playSound('defeat'),
  };

  return { playSound, sounds };
}

export default useChessSounds;
